import React from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'

// Elements
import HeroSecond from 'components/elements/HeroSecond'
import Image from 'components/shared/Image'

// Components
import SEO from 'components/shared/SEO'
import Layout from 'components/layout/Layout'
import Content from 'components/shared/Content'
import Team from 'components/elements/Team'

const Container = styled.div`
  margin-top: -10rem;
`

const Title = styled(Content)`
  & h2 {
    font-family: ${(props) => props.theme.font.family.secondaryThin};
    font-size: ${(props) => props.theme.font.size['55']};
    text-transform: uppercase;
    line-height: ${(props) => props.theme.font.size['60']};

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['35']};
      line-height: ${(props) => props.theme.font.size['40']};
    }
  }

  & h1 {
    font-size: ${(props) => props.theme.font.size['80']};
    line-height: ${(props) => props.theme.font.size['70']};
    position: relative;
    left: 60px;

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['65']};
      line-height: ${(props) => props.theme.font.size['40']};
    }
  }
`

const LogoTitle = styled(Content)`
  & h2 {
    font-size: ${(props) => props.theme.font.size['70']};
    text-transform: uppercase;
  }
`

const TextBible = styled(Content)`
  & p {
    font-family: ${(props) => props.theme.font.family.secondaryLight};
    font-size: ${(props) => props.theme.font.size.l};
    color: ${(props) => props.theme.color.text.contrast};
    text-transform: uppercase;
    text-align: right;

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['20']};
    }
  }
`

const Bron = styled(Content)`
  & h3 {
    font-size: ${(props) => props.theme.font.size['70']};
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.font.family.secondaryThin};

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['40']};
    }

    & span:first-child {
      float: right;
    }
  }

  & p {
    font-size: ${({ theme }) => theme.font.size.l};
    font-family: ${(props) => props.theme.font.family.secondaryThin};
    float: right;
    position: relative;
    top: -60px;
  }
`

const GroupTitle = styled(Content)`
  & h2 {
    font-size: ${(props) => props.theme.font.size['60']};
    text-transform: uppercase;

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['40']};
    }
  }
`

const GroupItem = styled.p`
  font-family: ${(props) => props.theme.font.family.secondaryBold};
  font-size: ${(props) => props.theme.font.size.l};
  color: ${(props) => props.theme.color.text.light};

  @media (max-width: 991px) {
    font-size: ${(props) => props.theme.font.size['20']};
  }
`

const FooterImage = styled(Image)`
  @media (max-width: 991px) {
    height: 300px;
  }

  @media (min-width: 992px) {
    min-height: 624px;
  }
`

const OurTitle = styled(Content)`
  & h2 {
    font-family: ${(props) => props.theme.font.family.secondaryThin};
    font-size: ${(props) => props.theme.font.size['55']};
    line-height: ${(props) => props.theme.font.size['60']};
    text-transform: uppercase;

    & span {
      font-family: ${(props) => props.theme.font.family.main};
      font-size: ${(props) => props.theme.font.size['70']};
      text-transform: uppercase;
      position: relative;
      left: 60px;
    }
  }
`

const Description = styled(Content)`
  & h3 {
    font-family: ${(props) => props.theme.font.family.secondaryBold};
    font-size: ${(props) => props.theme.font.size['50']};
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 3rem;
  }
`

const TitleCol = styled.div`
  @media (min-width: 992px) {
    z-index: 2;
  }
`

const TeamCol = styled.div`
  position: relative;

  @media (min-width: 1200px) {
    position: absolute;
    right: 80px;
  }
`

const CustomImage = styled(Image)`
  @media (max-width: 991px) {
    width: 100%;
  }
`

const PageTemplate = ({
  data: {
    page: { path, yoast_meta: yoast, acf },
  },
}) => {
  return (
    <Layout noChurch>
      <SEO yoast={yoast} path={path} article />

      <HeroSecond fields={acf.banner} withDescription />

      <section className="position-relative">
        <Container className="container color-background-light">
          <div className="row px-lg-3 pt-5 p-lg-5 align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0">
              <Title content={acf.whe_are.title} />
            </div>
            <div className="col-lg-7">
              <Content content={acf.whe_are.description} />
            </div>
          </div>

          <div className="row pl-lg-5 pt-3 pt-lg-5">
            <div className="col-lg-5 pr-5">
              <Image src={acf.whe_are.icon} className="mb-5" />

              <Content content={acf.whe_are.second_description} />
            </div>
            <div className="col-lg-7 text-right">
              <CustomImage src={acf.whe_are.zuilen} />
            </div>
          </div>
        </Container>
      </section>

      <section>
        <div className="container">
          <div className="row py-5">
            <div className="col-12 text-center">
              <LogoTitle content={acf.logo_explain.title} className="mb-5" />

              <CustomImage
                src={acf.logo_explain.logo_image}
                className="mb-5 pb-5"
              />

              <CustomImage src={acf.logo_explain.icons_image} />
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="container py-lg-5">
          <div className="row pt-5 justify-content-end">
            <div className="col-lg-8">
              <TextBible content={acf.bible_text.description} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <Bron content={acf.bible_text.bron} />
            </div>
          </div>
        </div>
      </section>

      <section className="color-background-main">
        <div className="container py-lg-4">
          <div className="row py-5">
            <div className="col-12 text-center">
              <GroupTitle content={acf.groups.title} className="mb-4 pb-lg-2" />
              {acf.groups.group.map(({ item }, index) => (
                <GroupItem className="mb-2" key={index}>
                  {item}
                </GroupItem>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="position-relative" style={{ overflow: 'hidden' }}>
        <div className="container py-5">
          <div className="row py-5 mt-lg-5 mb-lg-5">
            <TitleCol className="col-12 position-relative mb-5 mb-lg-0">
              <OurTitle content={acf.our_people.title} />
            </TitleCol>
            <TeamCol className="col-xl-6">
              <Team />
            </TeamCol>
          </div>

          {acf.our_people.description && (
            <div className="py-5">
              <div className="col-12 mx-auto mt-lg-5">
                <Description
                  content={acf.our_people.description}
                  className="pt-lg-5"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="pt-lg-5 mt-lg-5">
          <FooterImage className="w-100" src={acf.footerimage} />
        </div>
      </section>
    </Layout>
  )
}

export const pageQuery = graphql`
  query($wordpress_id: Int) {
    page: wordpressPage(wordpress_id: { eq: $wordpress_id }) {
      title
      path

      acf {
        banner {
          image {
            localFile {
              childImageSharp {
                fluid(quality: 100, maxWidth: 1920) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
          bibletext
        }

        whe_are {
          title
          description
          icon {
            localFile {
              publicURL
            }
          }
          second_description
          zuilen {
            localFile {
              childImageSharp {
                fluid(quality: 100, maxWidth: 600) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }

        logo_explain {
          title
          logo_image {
            localFile {
              publicURL
            }
          }
          icons_image {
            localFile {
              publicURL
            }
          }
        }

        bible_text {
          bron
          description
        }

        groups {
          title
          group {
            item
          }
        }

        our_people {
          title
          description
        }

        footerimage {
          localFile {
            childImageSharp {
              fluid(quality: 100, maxWidth: 1920) {
                ...GatsbyImageSharpFluid_withWebp_noBase64
              }
            }
          }
        }
      }

      yoast_meta {
        name
        content
        property
      }
    }
  }
`

export default PageTemplate
